import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Lock, Mail, CheckCircle, AlertCircle } from '../icons';
import { resetPassword, forgotPassword } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

function ResetPassword() {
  const { token } = useParams();
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  // The server answered "invalid or expired link": offer a new one right here,
  // since "Forgot password" only exists as a modal on the login page.
  const [linkDead, setLinkDead] = useState(false);
  const [email, setEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestDone, setRequestDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError(t('resetPassword.minLength')); return; }
    if (password !== confirm) { setError(t('resetPassword.noMatch')); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      // Password rules are checked above, so a 400 means the token is bad
      if (err.response?.status === 400) setLinkDead(true);
      else setError(err.response?.data?.error || t('resetPassword.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewLink = async (e) => {
    e.preventDefault();
    setError('');
    setRequesting(true);
    try {
      await forgotPassword(email);
      setRequestDone(true);
    } catch (err) {
      // e.g. the rate limit; the server never reveals whether the email exists
      setError(err.response?.data?.error || t('resetPassword.genericError'));
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Heart className="auth-icon" size={32} />
            <h1>{t('resetPassword.title')}</h1>
            <p>{t('resetPassword.subtitle')}</p>
          </div>

          {success ? (
            <div className="suspension-notice success">
              <CheckCircle size={40} className="success-icon" />
              <h2>{t('resetPassword.successTitle')}</h2>
              <p>{t('resetPassword.successText')}</p>
              <Link to="/login" className="btn btn-primary btn-block" style={{ marginTop: '16px' }}>
                {t('resetPassword.goToLogin')}
              </Link>
            </div>
          ) : linkDead ? (
            requestDone ? (
              <div className="suspension-notice success">
                <Mail size={40} className="success-icon" />
                <p>{t('resetPassword.newLinkSent')}</p>
                <Link to="/login" className="btn btn-primary btn-block" style={{ marginTop: '16px' }}>
                  {t('resetPassword.goToLogin')}
                </Link>
              </div>
            ) : (
              <>
                <div className="auth-error" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={16} /> {error || t('resetPassword.linkInvalid')}
                </div>
                <form onSubmit={handleRequestNewLink} className="auth-form">
                  <div className="form-group">
                    <label><Mail size={16} /> {t('resetPassword.emailLabel')}</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary btn-block" disabled={requesting}>
                    {requesting ? t('resetPassword.sending') : t('resetPassword.sendNewLink')}
                  </button>
                </form>
                <div className="auth-footer">
                  <p>{t('resetPassword.rememberPassword')} <Link to="/login">{t('auth.signIn')}</Link></p>
                </div>
              </>
            )
          ) : (
            <>
              {error && (
                <div className="auth-error" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label><Lock size={16} /> {t('resetPassword.newPassword')}</label>
                  <input
                    type="password"
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label><Lock size={16} /> {t('resetPassword.confirmPassword')}</label>
                  <input
                    type="password"
                    placeholder={t('resetPassword.confirmPlaceholder')}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
                </button>
              </form>

              <div className="auth-footer">
                <p>{t('resetPassword.rememberPassword')} <Link to="/login">{t('auth.signIn')}</Link></p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
